/**
 * A light, tabular numeral (DESIGN.md "Typography"): `score` is the 6rem
 * Score Numeral (4.5rem on phones), `figure-lg` the 3.5rem pillar-tile score,
 * `figure` the 2rem framework pass rate. `of` adds the 1.25rem "/ 100" in
 * helper gray. A missing value renders a dash, never "NaN" or "undefined".
 */
import clsx from 'clsx'
import type { BigNumberProps, BigNumberSize } from './types'

const SIZE: Record<BigNumberSize, string> = {
  score: 'text-score-num max-c-md:text-[4.5rem]',
  'figure-lg': 'text-figure-lg',
  figure: 'text-figure',
}

function display(value: BigNumberProps['value']): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? String(Math.round(value)) : null
  return value.trim() ? value : null
}

export function BigNumber({ value, of, size = 'score', suffix, as: Element = 'p', className }: BigNumberProps) {
  const shown = display(value)
  const after = suffix ?? (of !== undefined ? `/ ${of}` : null)
  return (
    <Element className={clsx('m-0 flex items-baseline gap-1.5', className)}>
      <strong className={clsx('font-light tabular-nums text-text-primary', SIZE[size])}>
        {shown ?? (
          <>
            <span aria-hidden="true">—</span>
            <span className="sr-only">Not available</span>
          </>
        )}
      </strong>
      {after && shown !== null ? (
        <span className="text-heading font-light text-text-helper">
          {of !== undefined && suffix === undefined ? (
            <>
              <span aria-hidden="true">{after}</span>
              <span className="sr-only">out of {of}</span>
            </>
          ) : (
            after
          )}
        </span>
      ) : null}
    </Element>
  )
}

export default BigNumber
